import { Button, HorizontalDivider, Icon, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { Card, SubTitle } from '@hero/atoms';
import { DuplicateModal } from '@hero/components/modals';
import { reactivateAutomationFormData } from '@hero/components/organisms/Automation/constants';
import { AUTOMATIONS_TAB, CARDISLE_GREETING_CARD, PAYMENT_METHOD_TYPES } from '@hero/constants';
import { useNotification } from '@hero/store/hooks';
import { getAddonData, getFeatureStatus } from '@hero/utils';
import { automationReactivateSchema } from '@hero/validations';

import { isActive, isDeactivate, isDraftOrReadyToSend, isExpired, isTeams } from './constants';
import * as S from './style';

function AutomationStatusSummary({
  feature,
  analytics,
  averageAcceptanceRate,
  onDuplicate,
  onDeactivate,
  onReactivate,
  duplicateText,
  setActiveTab,
  spendingSummary,
  giftCount,
  contactType,
  giftMessage
}) {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const status = getFeatureStatus(feature);
  const digitalGiftCard = getAddonData(feature?.addons, CARDISLE_GREETING_CARD);
  const { estimatedCharges } = spendingSummary ?? {};

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const onDeactivateModal = () => {
    showNotification({
      popupStyle: {
        maxWidth: '500px'
      },
      title: t('areYouSure'),
      message:
        feature?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard ? (
          <Trans i18nKey="expireAutomationPromptMessageCC" components={{ b: <b /> }} />
        ) : (
          <Trans i18nKey="expireAutomationPromptMessageHouseAccount" components={{ b: <b /> }} />
        ),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      primaryButtonProps: { color: 'error', children: t('yesDeactivate') },
      secondaryButtonProps: { children: t('keepActive') },
      onSubmit: onDeactivate
    });
  };

  const onReactivateModal = async () => {
    showNotification({
      notification: {
        title: (
          <Typography as="div" variant="body2Bold" style={{ display: 'flex', gap: 4 }}>
            {t('totalEstimatedSpend')}
            <Typography variant="body2" color="fg" emphasis="weak">
              ({t('perRecipient').toLocaleLowerCase()})
            </Typography>
          </Typography>
        ),
        message: <Typography variant="body2Bold">{`$${analytics?.total}`}</Typography>,
        status: 'info',
        isClosable: true
      },
      message: t('reactivateModalDescRedemption'),
      notificationImage: <Icon name="calendar" size="xxl" />,
      formData: reactivateAutomationFormData,
      formValidationSchema: automationReactivateSchema,
      formStyle: {
        background: 'var(--color-bg-default)',
        borderRadius: 'var(--radius-m)',
        border: '1px solid var(--color-accent-secondary-weaker)',
        padding: 16
      },
      primaryButtonProps: { children: t('reactivate') },
      secondaryButtonProps: { children: t('cancel') },
      onSubmit: onReactivate
    });
  };

  return (
    <Card>
      <SubTitle
        title={t('statusSummary')}
        status={status}
        style={{ marginBottom: '16px' }}
        links={
          <Button
            variant="link"
            startIcon={<Icon name="analytics" />}
            onClick={() => setActiveTab(AUTOMATIONS_TAB.analytics)}
          >
            {t('seeAnalytics')}
          </Button>
        }
      />
      <S.StatusInfoWrapper>
        <S.StatusBadge>
          <Icon name="email" color="var(--color-accent-primary-default)" />
          <Typography variant="body1Bold">{analytics?.total}</Typography>
          <Typography variant="caption1">{t('sent')}</Typography>
        </S.StatusBadge>
        <S.StatusBadge>
          <Icon name="giftbox-opened" color="var(--color-accent-primary-strongest)" />
          <Typography variant="body1Bold">{analytics?.opened}</Typography>
          <Typography variant="caption1">{t('opened')}</Typography>
        </S.StatusBadge>
        <S.StatusBadge>
          <Icon name="gift-box" color="var(--color-info-default)" />
          <Typography variant="body1Bold">{analytics?.unopened}</Typography>
          <Typography variant="caption1">{t('unopened')}</Typography>
        </S.StatusBadge>
      </S.StatusInfoWrapper>

      <HorizontalDivider $margin="16px 0" />

      <S.CardFooter>
        {!isDraftOrReadyToSend(status) && (
          <S.AvgTimeContainer>
            <Typography variant="body2Bold" color="fg" emphasis="weaker">
              {t('avgAcceptanceTime')}:
            </Typography>
            {averageAcceptanceRate ? (
              <Typography
                as="div"
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                variant="body2Bold"
              >
                <Icon name="clock" />
                {averageAcceptanceRate}
              </Typography>
            ) : (
              <Typography variant="body2Bold" color="fg" emphasis="weaker">
                {t('notAcceptedYet')}
              </Typography>
            )}
          </S.AvgTimeContainer>
        )}

        <S.ButtonWrapper>
          {isActive(status) && (
            <Button
              size="sm"
              color="error"
              variant="outlined"
              onClick={onDeactivateModal}
              startIcon={<Icon name="slash" />}
              block
            >
              {t('deActive')}
            </Button>
          )}
          {(isExpired(status) || isDeactivate(status)) && (
            <Button
              size="sm"
              variant="outlined"
              color="secondary"
              onClick={onReactivateModal}
              startIcon={<Icon name="gift-history" />}
              block
            >
              {t('reactivate')}
            </Button>
          )}
          {!isTeams(feature) && (
            <Button
              size="sm"
              variant="outlined"
              onClick={() => setDuplicateModalOpen(true)}
              startIcon={<Icon name="copy-paste" />}
              block
            >
              {t('duplicate')}
            </Button>
          )}
        </S.ButtonWrapper>
      </S.CardFooter>

      <DuplicateModal
        isOpen={duplicateModalOpen}
        onOpen={setDuplicateModalOpen}
        onSubmit={onDuplicate}
        featureType="automation"
        duplicateText={duplicateText}
        recipientCount={estimatedCharges?.recipientCount}
        giftCount={giftCount}
        contactType={contactType}
        giftMessage={giftMessage}
        giftPrice={estimatedCharges?.baseProductPrice}
        digitalGiftCard={digitalGiftCard}
      />
    </Card>
  );
}

AutomationStatusSummary.propTypes = {
  feature: PropTypes.shape({
    paymentMethod: PropTypes.shape({
      type: PropTypes.string
    }),
    addons: PropTypes.string
  }),
  analytics: PropTypes.shape({
    total: PropTypes.number,
    opened: PropTypes.number,
    unopened: PropTypes.number
  }),
  averageAcceptanceRate: PropTypes.number,
  onDuplicate: PropTypes.func,
  onDeactivate: PropTypes.func,
  onReactivate: PropTypes.func,
  duplicateText: PropTypes.string,
  setActiveTab: PropTypes.func,
  spendingSummary: PropTypes.objectOf(PropTypes.any),
  giftCount: PropTypes.number,
  contactType: PropTypes.string,
  giftMessage: PropTypes.string
};

export { AutomationStatusSummary };
